<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const sections = [
  {
    title: '↩️ Beda Refund dan Void',
    icon: 'ri-git-compare-line',
    color: 'primary',
    content: `
      <p class="mb-4">
        Dua istilah ini sering tertukar, padahal dipakai untuk situasi yang berbeda:
      </p>
      <VRow class="mb-4">
        <VCol cols="12" md="6">
          <VCard variant="outlined">
            <VCardText>
              <div class="d-flex align-center mb-2">
                <VChip color="warning" variant="tonal" size="small" class="me-2">Void</VChip>
                <strong>Batalkan Transaksi</strong>
              </div>
              <p class="text-body-2 mb-0">
                Dipakai untuk transaksi yang <strong>belum final/salah input</strong> —
                mis. kasir salah pencet, order dibatalkan sebelum uang berpindah tangan
                sungguhan. Order jadi berstatus <em>Void</em>, bukan dihapus dari sistem.
              </p>
            </VCardText>
          </VCard>
        </VCol>
        <VCol cols="12" md="6">
          <VCard variant="outlined">
            <VCardText>
              <div class="d-flex align-center mb-2">
                <VChip color="error" variant="tonal" size="small" class="me-2">Refund</VChip>
                <strong>Kembalikan Uang</strong>
              </div>
              <p class="text-body-2 mb-0">
                Dipakai untuk transaksi yang <strong>sudah selesai (completed)</strong> —
                uang sudah diterima, barang sudah diserahkan, tapi belakangan perlu
                dikembalikan sebagian/seluruhnya (mis. pelanggan komplain, barang rusak).
              </p>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
      <VAlert type="info" variant="tonal">
        💡 Ringkasnya: <strong>Void</strong> = "transaksi ini belum benar-benar terjadi,
        batalkan". <strong>Refund</strong> = "transaksi ini sudah terjadi, sekarang uangnya
        perlu dikembalikan".
      </VAlert>
    `,
  },
  {
    title: '🌐 Tersedia di Semua Paket',
    icon: 'ri-checkbox-circle-line',
    color: 'success',
    content: `
      <p class="mb-0">
        Berbeda dari Customer Display System, <strong>Refund Berotorisasi tersedia di
        SEMUA paket</strong> (Starter, Pro, maupun Premium) — bukan fitur eksklusif yang
        perlu upgrade paket untuk memakainya.
      </p>
    `,
  },
  {
    title: '📝 Cara Memproses Refund',
    icon: 'ri-refund-2-line',
    color: 'error',
    content: `
      <ol class="mb-4">
        <li class="mb-2">Cari transaksi lama lewat menu <strong>Penjualan → Daftar
          Order</strong>, buka detail order yang berstatus <strong>"Selesai"</strong></li>
        <li class="mb-2">Klik tombol <strong>"Refund Order Ini"</strong></li>
        <li class="mb-2">Pada langkah pertama, <strong>centang item</strong> yang mau
          di-refund dan atur <strong>jumlahnya</strong> (boleh sebagian, tidak harus semua
          item/qty dalam order itu)</li>
        <li class="mb-2">Isi <strong>"Alasan Refund"</strong> — wajib diisi, tidak bisa
          dikosongkan</li>
        <li class="mb-2">Klik <strong>"Lanjutkan"</strong> — masuk ke langkah kedua: masukkan
          <strong>PIN Manager/Owner</strong> lewat keypad untuk mengotorisasi refund ini</li>
        <li class="mb-0">Refund diproses begitu PIN benar & sesuai izin</li>
      </ol>
      <VAlert type="warning" variant="tonal">
        ⚠️ Kasir biasa <strong>tidak bisa</strong> menyetujui refund sendiri — wajib PIN
        dari user berperan Manager/Owner (atau role lain yang diberi izin otorisasi
        refund). Ini sengaja dirancang sebagai kontrol, supaya tidak ada refund
        sembarangan tanpa sepengetahuan atasan.
      </VAlert>
    `,
  },
  {
    title: '📦 Pilihan "Restock" per Item',
    icon: 'ri-inbox-archive-line',
    color: 'warning',
    content: `
      <p class="mb-4">
        Di samping tiap item yang di-refund, ada centang <strong>"Restock"</strong> —
        menentukan apakah stok/bahan baku item itu dikembalikan ke inventory atau tidak.
      </p>
      <table class="userguide-table mb-4">
        <thead>
          <tr><th>Kondisi</th><th>Centang Restock?</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>Barang dikembalikan pelanggan dalam kondisi masih bisa dijual lagi
              (mis. salah beli, belum dibuka/dipakai)</td>
            <td>✅ <strong>Centang</strong> — stok bertambah kembali</td>
          </tr>
          <tr>
            <td>Barang rusak, sudah dikonsumsi/dipakai, atau memang tidak layak dijual
              ulang (mis. makanan sudah dimakan sebagian, kualitas komplain)</td>
            <td>❌ <strong>Jangan dicentang</strong> — stok tidak bertambah</td>
          </tr>
        </tbody>
      </table>
      <VAlert type="info" variant="tonal">
        💡 Pilihan ini per item, jadi dalam 1 refund boleh campur — sebagian item
        di-restock, sebagian lagi tidak, tergantung kondisi barangnya masing-masing.
      </VAlert>
    `,
  },
  {
    title: '💵 Refund Selalu Dianggap Tunai',
    icon: 'ri-money-dollar-circle-line',
    color: 'secondary',
    content: `
      <p class="mb-4">
        Poin penting: <strong>apa pun metode pembayaran asli transaksi</strong> (Tunai,
        QRIS, Kartu, Transfer), uang refund <strong>selalu dianggap keluar sebagai uang
        tunai</strong> dari laci kasir shift yang sedang aktif saat refund diproses.
      </p>
      <p class="mb-4">
        Ini berarti nominal refund otomatis mengurangi angka <strong>"Kas Seharusnya"</strong>
        pada rekonsiliasi kas shift tersebut, terlepas dari shift mana transaksi aslinya
        dibuat.
      </p>
      <VAlert type="warning" variant="tonal">
        ⚠️ Kasir yang menutup shift wajib sadar: kalau ada refund diproses selama shift
        berjalan, jumlah uang tunai fisik di laci akan lebih sedikit dari total penjualan
        tunai shift itu sendiri — bagian itu memang sudah diperhitungkan otomatis di
        "Kas Seharusnya", tapi tetap penting dipahami saat menghitung kas fisik secara
        manual.
      </VAlert>
    `,
  },
]
</script>

<template>
  <div>
    <RouterLink to="/userguide">
      <VBtn
        variant="text"
        prepend-icon="ri-arrow-left-line"
        class="mb-2"
      >
        Kembali ke Daftar Modul
      </VBtn>
    </RouterLink>

    <div class="d-flex align-center mb-4">
      <VAvatar
        color="error"
        variant="tonal"
        size="64"
        rounded="lg"
        class="me-4"
      >
        <VIcon
          icon="ri-refund-2-line"
          size="36"
        />
      </VAvatar>
      <div>
        <h4 class="text-h4 font-weight-bold mb-1">
          Refund Berotorisasi
        </h4>
        <p class="text-body-1 text-medium-emphasis mb-0">
          Kembalikan uang pelanggan untuk transaksi yang sudah selesai, dengan persetujuan
          PIN Manager/Owner
        </p>
      </div>
    </div>

    <VAlert
      type="success"
      variant="tonal"
      class="mb-6"
      icon="ri-checkbox-circle-line"
    >
      Tersedia di <strong>semua paket</strong> — Starter, Pro, maupun Premium.
    </VAlert>

    <VExpansionPanels
      variant="accordion"
      multiple
    >
      <VExpansionPanel
        v-for="(section, index) in sections"
        :key="index"
      >
        <VExpansionPanelTitle>
          <VIcon
            :icon="section.icon"
            :color="section.color"
            class="me-3"
          />
          {{ section.title }}
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          <div v-html="section.content" />
        </VExpansionPanelText>
      </VExpansionPanel>
    </VExpansionPanels>

    <!-- FAQ -->
    <h5 class="text-h5 font-weight-bold mt-6 mb-3">
      ❓ Pertanyaan yang Sering Ditanyakan
    </h5>
    <VExpansionPanels variant="accordion">
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Bisakah kasir biasa menyetujui refund sendiri tanpa PIN Manager/Owner?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Tidak. Setiap refund wajib diotorisasi lewat PIN user berperan Manager/Owner
          (atau role lain dengan izin khusus) — ini kontrol yang sengaja tidak bisa
          dilewati, supaya tidak ada refund diproses tanpa sepengetahuan atasan.
        </VExpansionPanelText>
      </VExpansionPanel>
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Bisakah refund sebagian, tidak seluruh order?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Bisa. Anda memilih sendiri item mana dan berapa jumlahnya yang mau di-refund —
          tidak harus seluruh isi order tersebut.
        </VExpansionPanelText>
      </VExpansionPanel>
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Order dengan pembayaran QRIS di-refund — apakah uangnya kembali ke QRIS pelanggan?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Di dalam sistem, refund SELALU dicatat sebagai uang tunai keluar dari laci
          shift yang memproses refund — apa pun metode pembayaran aslinya. Bagaimana
          uangnya benar-benar dikembalikan ke pelanggan (tunai langsung, transfer manual,
          dsb) adalah keputusan operasional di luar sistem, sistem hanya mencatat dampaknya
          ke kas.
        </VExpansionPanelText>
      </VExpansionPanel>
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Order yang belum selesai (masih pending) bisa di-refund?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Tidak — tombol Refund hanya muncul untuk order berstatus "Selesai". Untuk order
          yang belum selesai/salah input, gunakan Void, bukan Refund (lihat penjelasan
          beda keduanya di bagian atas halaman ini).
        </VExpansionPanelText>
      </VExpansionPanel>
    </VExpansionPanels>

    <!-- Related -->
    <h5 class="text-h5 font-weight-bold mt-6 mb-3">
      🔗 Halaman Terkait
    </h5>
    <VRow>
      <VCol
        v-for="link in [
          { title: 'Customer Display System', sub: 'Layar kedua menghadap pelanggan (Premium)', icon: 'ri-tv-2-line', color: 'primary', to: { name: 'userguide-kasir-lanjutan-cds' } },
          { title: 'Penjualan', sub: 'Manage order dan shift kasir', icon: 'ri-shopping-cart-2-line', color: 'success', to: { name: 'userguide-operasional-penjualan' } },
          { title: 'POS Kasir', sub: 'Panduan lengkap kasir dan transaksi', icon: 'ri-shopping-cart-2-line', color: 'primary', to: { name: 'userguide-operasional-pos-kasir' } },
        ]"
        :key="link.title"
        cols="12"
        md="6"
      >
        <RouterLink
          :to="link.to"
          class="text-decoration-none"
        >
          <VCard
            variant="outlined"
            hover
          >
            <VCardText class="d-flex align-center gap-3 py-3">
              <VAvatar
                :color="link.color"
                variant="tonal"
                size="40"
                rounded
              >
                <VIcon
                  :icon="link.icon"
                  size="22"
                />
              </VAvatar>
              <div>
                <p class="text-body-2 font-weight-bold mb-0">
                  {{ link.title }}
                </p>
                <p class="text-caption text-medium-emphasis mb-0">
                  {{ link.sub }}
                </p>
              </div>
            </VCardText>
          </VCard>
        </RouterLink>
      </VCol>
    </VRow>
  </div>
</template>

<style scoped>
.userguide-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.userguide-table th,
.userguide-table td {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  padding: 8px 12px;
  text-align: left;
  vertical-align: top;
}

.userguide-table th {
  background: rgba(var(--v-theme-on-surface), 0.04);
  font-weight: 600;
}
</style>
